import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { getDataSourceOptions } from "@/api";
import { EntityDetailPage } from "@/components/build/EntityDetailPage";
import { EditWithAiButton } from "@/features/build/withAi/EditWithAiButton";
import { FilePreviewPanel } from "@/features/files/FilePreviewPanel";
import { dataSourceFile } from "@/features/files/filePreview";
import { DataSourceAccessTab } from "./DataSourceAccessTab";
import { DataSourceContentTab } from "./DataSourceContentTab";
import { DataSourceOverviewForm } from "./DataSourceOverviewForm";
import { useDeleteDataSource } from "./useDeleteDataSource";

/** Build > Data sources > detail: the shared EntityDetailPage scaffold; Overview (form), Content (files/notes), Access (membership). */
export function DataSourceDetailPage() {
  const { t } = useTranslation(["build", "dataSources"]);
  const navigate = useNavigate();
  const { uuid = "", tab } = useParams();
  const [previewUuid, setPreviewUuid] = useState<string | null>(null);
  const source = useQuery(getDataSourceOptions({ path: { collection_uuid: uuid } }));
  const { onDelete, dialog } = useDeleteDataSource(() => navigate("/build/data-sources"));
  const data = source.data?.data;

  return (
    <EntityDetailPage
      listPath="/build/data-sources"
      rootLabel={t("dataSources:title")}
      name={data?.name ?? ""}
      isPending={source.isPending}
      isError={source.isError}
      notFoundLabel={t("dataSources:detail.notFound")}
      tabs={[
        {
          value: "overview",
          label: t("dataSources:tabs.overview"),
          content: data && <DataSourceOverviewForm source={data} />,
        },
        {
          value: "content",
          label: t("dataSources:tabs.content"),
          content: data && <DataSourceContentTab source={data} previewUuid={previewUuid} onPreview={setPreviewUuid} />,
        },
        {
          value: "access",
          label: t("dataSources:tabs.access"),
          content: data && <DataSourceAccessTab source={data} />,
        },
      ]}
      canDelete={data?.canManage ?? false}
      onDelete={() => data && onDelete(data)}
      deleteDialog={dialog}
      extraActions={data?.canEdit ? <EditWithAiButton entityType="data_source" uuid={data.uuid} /> : undefined}
      aside={
        data &&
        previewUuid &&
        tab === "content" && (
          <FilePreviewPanel
            key={previewUuid}
            files={data.files ?? []}
            fileUuid={previewUuid}
            fileFor={(file) => dataSourceFile(data.uuid, file)}
            listLabel={t("dataSources:previewList")}
            onOpen={setPreviewUuid}
            onClose={() => setPreviewUuid(null)}
          />
        )
      }
    />
  );
}
